<script setup lang="ts">
import {
    SwitchRoot,
    type SwitchRootEmits,
    type SwitchRootProps,
    SwitchThumb,
    useForwardPropsEmits
} from 'reka-ui';
import { computed, type HTMLAttributes } from 'vue';

import { cn } from '@/lib/utils';

type SwitchSize = 'xs' | 'sm' | 'md';

const props = withDefaults(
    defineProps<
        SwitchRootProps & {
            class?: HTMLAttributes['class'];
            size?: SwitchSize;
        }
    >(),
    {
        size: 'sm'
    }
);

const emits = defineEmits<SwitchRootEmits>();

const delegatedProps = computed(() => {
    const { class: _, size: __, ...delegated } = props;

    return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const switchSizeClasses = computed(() => {
    switch (props.size) {
        case 'xs':
            return 'h-4 w-8';
        case 'md':
            return 'h-7 w-14';
        case 'sm':
        default:
            return 'h-5 w-10';
    }
});

const thumbSizeClasses = computed(() => {
    switch (props.size) {
        case 'xs':
            return 'h-3 w-3 data-[state=checked]:translate-x-4';
        case 'md':
            return 'h-6 w-6 data-[state=checked]:translate-x-7';
        case 'sm':
        default:
            return 'h-4 w-4 data-[state=checked]:translate-x-5';
    }
});
</script>

<template>
    <SwitchRoot
        v-bind="forwarded"
        :class="
            cn(
                'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-foreground data-[state=unchecked]:bg-primary40 dark:focus-visible:ring-primary40 dark:focus-visible:ring-offset-foreground dark:data-[state=checked]:bg-foreground dark:data-[state=unchecked]:bg-primary50',
                switchSizeClasses,
                props.class
            )
        "
    >
        <SwitchThumb
            :class="
                cn(
                    'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform dark:bg-background',
                    thumbSizeClasses
                )
            "
        >
            <slot name="thumb" />
        </SwitchThumb>
    </SwitchRoot>
</template>
